import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Tarjeta } from '../componentes/ui/basicos';
import { Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { importeParaMirar } from '../lib/numero';

interface Tipo {
  readonly id: string;
  readonly nombre: string;
  readonly activo: boolean;
}

interface Conversion {
  readonly destinoId: string;
  readonly origenId: string;
}

interface Pendiente {
  readonly id: string;
  readonly numero: string;
  readonly emitidoEn: string;
  readonly total: string;
  readonly terceroId: string | null;
  readonly nombreDelCliente: string | null;
}

/** Suma de importes exactos, en enteros. Es plata: no pasa por coma flotante (regla 2). */
function sumar(importes: readonly string[]): string {
  const bruto = importes.reduce((total, texto) => {
    const [entera = '0', fraccion = ''] = texto.split('.');
    return total + BigInt(entera) * 10_000n + BigInt(fraccion.padEnd(4, '0').slice(0, 4));
  }, 0n);
  return `${String(bruto / 10_000n)}.${String(bruto % 10_000n).padStart(4, '0')}`;
}

/**
 * Facturar lo que se entregó.
 *
 * ## Qué contesta
 *
 * **«Qué me falta facturar».** Es la pregunta del mayorista que despacha con remito durante la
 * quincena y factura a fin de mes, y la razón por la que esta pantalla existe separada del
 * mostrador: no se está vendiendo nada nuevo, se está documentando lo que ya salió.
 *
 * ## Dos cosas que la pantalla tiene que impedir
 *
 * 1. **Mezclar clientes.** El servidor lo rechaza, pero descubrirlo al confirmar es tarde: acá se
 *    agrupa por cliente y sólo se puede elegir dentro de un grupo.
 * 2. **Creer que se está cobrando.** Lo que se emite acá es el documento; el cobro va después por
 *    su camino. Por eso factura **en cuenta corriente** y lo dice.
 */
export function Conversiones(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const [destino, setDestino] = useState('');
  const [elegidos, setElegidos] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);

  const tipos = useQuery({
    queryKey: ['tipos-de-comprobante'],
    queryFn: () => pedir<Tipo[]>('/tipos-de-comprobante'),
  });
  const circuito = useQuery({
    queryKey: ['circuito'],
    queryFn: () => pedir<Conversion[]>('/tipos-de-comprobante/circuito'),
  });

  // Sólo los tipos que declaran de dónde pueden nacer: los demás no se pueden convertir en nada, y
  // ofrecerlos sería prometer una operación que va a fallar.
  const conOrigen = new Set((circuito.data ?? []).map((fila) => fila.destinoId));
  const destinos = (tipos.data ?? []).filter((tipo) => tipo.activo && conOrigen.has(tipo.id));
  const destinoActual = destinos.find((tipo) => tipo.id === destino) ?? destinos[0];

  const pendientes = useQuery({
    queryKey: ['pendientes-de-convertir', destinoActual?.id],
    enabled: destinoActual !== undefined,
    queryFn: () => pedir<Pendiente[]>(`/conversiones/pendientes?tipoId=${destinoActual?.id ?? ''}`),
  });

  const puntos = useQuery({
    queryKey: ['puntos-de-venta'],
    queryFn: () => pedir<{ id: string }[]>('/puntos-de-venta'),
  });
  const puestos = useQuery({
    queryKey: ['puestos-de-caja'],
    queryFn: () => pedir<{ id: string }[]>('/puestos-de-caja'),
  });

  const seleccionados = (pendientes.data ?? []).filter((fila) => elegidos.includes(fila.id));
  const total = sumar(seleccionados.map((fila) => fila.total));

  const facturar = useMutation({
    mutationFn: () =>
      pedir<{ numero: string }>('/conversiones', {
        method: 'POST',
        cuerpo: {
          tipoDestinoId: destinoActual?.id ?? '',
          origenes: elegidos,
          puntoDeVentaId: puntos.data?.[0]?.id ?? '',
          puestoDeCajaId: puestos.data?.[0]?.id ?? '',
          autorizarExcesoDeCredito: true,
          cobros: [{ medioDePago: 'cuenta_corriente', importe: total }],
        },
      }),
    onSuccess: async (respuesta) => {
      setHecho(t('conversiones.hecho', { numero: respuesta.numero }));
      setElegidos([]);
      setError(null);
      await cola.invalidateQueries({ queryKey: ['pendientes-de-convertir'] });
    },
    onError: (fallo: unknown) => {
      setHecho(null);
      setError(textoDeError(fallo));
    },
  });

  if (tipos.isPending || circuito.isPending) return <Cargando />;
  if (tipos.isError) return <Problema error={tipos.error} />;

  if (destinos.length === 0) {
    return (
      <>
        <Encabezado titulo={t('conversiones.titulo')} descripcion={t('conversiones.descripcion')} />
        <Tarjeta>
          <Vacio
            titulo={t('conversiones.sinCircuito')}
            descripcion={t('conversiones.comoSeArma')}
          />
        </Tarjeta>
      </>
    );
  }

  // Agrupado por cliente: el servidor rechaza mezclar, y descubrirlo al confirmar sería tarde.
  const porCliente = new Map<string, Pendiente[]>();
  for (const fila of pendientes.data ?? []) {
    const clave = fila.terceroId ?? '';
    porCliente.set(clave, [...(porCliente.get(clave) ?? []), fila]);
  }
  const clienteElegido = seleccionados[0]?.terceroId ?? null;

  return (
    <>
      <Encabezado titulo={t('conversiones.titulo')} descripcion={t('conversiones.descripcion')} />

      <Tarjeta>
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <Selector
            etiqueta={t('conversiones.queSeEmite')}
            value={destinoActual?.id ?? ''}
            onChange={(evento) => {
              setDestino(evento.target.value);
              setElegidos([]);
            }}
          >
            {destinos.map((tipo) => (
              <option key={tipo.id} value={tipo.id}>
                {tipo.nombre}
              </option>
            ))}
          </Selector>
          <Boton
            tono="principal"
            cargando={facturar.isPending}
            disabled={elegidos.length === 0}
            onClick={() => {
              facturar.mutate();
            }}
          >
            {t('conversiones.facturar', { cuantos: elegidos.length })}
          </Boton>
          {elegidos.length === 0 ? null : (
            <span className="text-cuerpo">
              {t('conversiones.total')}:{' '}
              <span className="cifra font-medium">{importeParaMirar(total, i18n.language)}</span>
            </span>
          )}
        </div>

        {error === null ? null : (
          <p role="alert" className="mb-2 text-apoyo text-mal">
            {error}
          </p>
        )}
        {hecho === null ? null : (
          <p role="status" className="mb-2 text-apoyo text-bien">
            {hecho}
          </p>
        )}

        {pendientes.isPending ? <Cargando /> : null}
        {pendientes.isSuccess && pendientes.data.length === 0 ? (
          <Vacio titulo={t('conversiones.nadaPendiente')} />
        ) : null}

        {[...porCliente.entries()].map(([clienteId, filas]) => (
          <div key={clienteId} className="mb-4">
            {/* Agrupar por cliente sin nombrarlo no sirve: el nombre está congelado en el
                comprobante desde E5, justamente para poder decirlo sin ir a buscarlo. */}
            <p className="mb-1 text-apoyo font-semibold uppercase tracking-wider text-texto-tenue">
              {filas[0]?.nombreDelCliente ?? t('conversiones.sinCliente')}
            </p>
            <ul className="flex flex-col divide-y divide-borde-tenue">
              {filas.map((fila) => {
                // Sólo se puede elegir dentro de un cliente: mezclar lo rechaza el servidor, y la
                // pantalla no tiene por qué dejar llegar hasta ahí.
                const bloqueado = clienteElegido !== null && fila.terceroId !== clienteElegido;
                return (
                  <li key={fila.id} className="flex items-center gap-3 py-2 text-cuerpo">
                    <input
                      type="checkbox"
                      id={`convertir-${fila.id}`}
                      disabled={bloqueado}
                      checked={elegidos.includes(fila.id)}
                      onChange={(evento) => {
                        setElegidos((previos) =>
                          evento.target.checked
                            ? [...previos, fila.id]
                            : previos.filter((id) => id !== fila.id),
                        );
                      }}
                    />
                    <label
                      htmlFor={`convertir-${fila.id}`}
                      className={`min-w-0 flex-1 ${bloqueado ? 'text-texto-tenue' : ''}`}
                    >
                      {t('conversiones.numero', { numero: fila.numero })}
                      <span className="block text-apoyo text-texto-tenue">
                        {fila.emitidoEn.slice(0, 10)}
                      </span>
                    </label>
                    <span className="cifra">{importeParaMirar(fila.total, i18n.language)}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <p className="text-apoyo text-texto-tenue">{t('conversiones.enCuenta')}</p>
      </Tarjeta>
    </>
  );
}
